import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Pause as HoldIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  Message as MessageIcon
} from '@mui/icons-material';

const QueryActionDialog = ({ 
  open, 
  onClose, 
  query, 
  actionType, // 'AP', 'RJ', 'HO' for customer queries, or status values for website messages
  onAction 
}) => {
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const MAX_REMARKS_LENGTH = 500;

  useEffect(() => {
    if (open) {
      setRemarks('');
      setError('');
      setCharCount(0);
      setShowConfirmation(false);
    }
  }, [open]);

  if (!query) return null;

  const getActionConfig = (actionType) => {
    console.log('actionType ', actionType);
    
    // Customer query actions
    switch (actionType) {
      case 'AP':
        return {
          title: 'Approve Query',
          buttonText: 'Approve',
          buttonColor: 'success',
          buttonIcon: <ApproveIcon />,
          status: 'APPROVED'
        };
      case 'RJ':
        return {
          title: 'Reject Query',
          buttonText: 'Reject',
          buttonColor: 'error',
          buttonIcon: <RejectIcon />,
          status: 'REJECTED'
        };
      case 'HO':
        return {
          title: 'Hold Query',
          buttonText: 'Hold',
          buttonColor: 'warning',
          buttonIcon: <HoldIcon />,
          status: 'HOLD'
        };
    }
    
    // Website message status changes
    const websiteStatusConfig = {
      'pending': {
        title: 'Mark as Pending Response',
        buttonText: 'Mark Pending',
        buttonColor: 'warning',
        buttonIcon: <HoldIcon />,
        status: 'PENDING_RESPONSE'
      },
      'in_progress': {
        title: 'Mark as In Progress',
        buttonText: 'Mark In Progress',
        buttonColor: 'info',
        buttonIcon: <HoldIcon />,
        status: 'IN_PROGRESS'
      },
      'acknowledged': {
        title: 'Mark as Acknowledged',
        buttonText: 'Mark Acknowledged',
        buttonColor: 'primary',
        buttonIcon: <ApproveIcon />,
        status: 'ACKNOWLEDGED'
      },
      'resolved': {
        title: 'Mark as Resolved',
        buttonText: 'Mark Resolved',
        buttonColor: 'success',
        buttonIcon: <ApproveIcon />,
        status: 'RESOLVED'
      },
      'closed': {
        title: 'Mark as Closed',
        buttonText: 'Mark Closed',
        buttonColor: 'default',
        buttonIcon: <CloseIcon />,
        status: 'CLOSED'
      },
      'spam': {
        title: 'Mark as Spam',
        buttonText: 'Mark Spam',
        buttonColor: 'error',
        buttonIcon: <RejectIcon />,
        status: 'SPAM'
      }
    };
    
    return websiteStatusConfig[actionType] || {
      title: 'Query Action',
      buttonText: 'Submit',
      buttonColor: 'primary',
      buttonIcon: null,
      status: ''
    };
  };

  const actionConfig = getActionConfig(actionType);

  const handleSubmit = async () => {
    if (!remarks.trim()) {
      setError('Please provide remarks for this action.');
      return;
    }

    if (remarks.trim().length < 10) {
      setError('Remarks must be at least 10 characters long.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onAction(query.id, remarks.trim(), actionType);
      setShowConfirmation(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.message || 'An error occurred while processing the action.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setRemarks('');
    setError('');
    setCharCount(0);
    setLoading(false);
    setShowConfirmation(false);
    onClose();
  };

  const handleRemarksChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_REMARKS_LENGTH) {
      setRemarks(value);
      setCharCount(value.length);
      setError('');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
      disableBackdropClick={loading}
    >
      <DialogTitle sx={{ 
        bgcolor: actionConfig.buttonColor === 'success' ? 'success.light' : 
                 actionConfig.buttonColor === 'error' ? 'error.light' : 
                 actionConfig.buttonColor === 'warning' ? 'warning.light' : 'primary.light',
        color: 'white'
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {actionConfig.buttonIcon}
            <Typography variant="h6" component="div">
              {actionConfig.title}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {showConfirmation && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Query {actionConfig.status.toLowerCase()} successfully!
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Query Summary */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MessageIcon color="primary" />
            {query.customer ? 'Query Summary' : 'Message Summary'}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {query.customer ? (
              // Customer Query Summary
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Customer:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {query.customer?.owner_name}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Company:
                  </Typography>
                  <Typography variant="body2">
                    {query.customer?.company_name}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Email:
                  </Typography>
                  <Typography variant="body2">
                    {query.customer?.owner_email}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Query Type:
                  </Typography>
                  <Typography variant="body2">
                    {query.query_type_verbose || 'General'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Status:
                  </Typography>
                  <Chip 
                    label={query.status_verbose || query.status} 
                    color={actionConfig.buttonColor}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Content:
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {query.content}
                  </Typography>
                </Box>
              </>
            ) : (
              // Website Message Summary
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Name:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {query.full_name}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Company:
                  </Typography>
                  <Typography variant="body2">
                    {query.company_name || 'N/A'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Email:
                  </Typography>
                  <Typography variant="body2">
                    {query.email}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Phone:
                  </Typography>
                  <Typography variant="body2">
                    {query.phone_number || 'N/A'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Status:
                  </Typography>
                  <Chip 
                    label={query.status_verbose || query.status} 
                    color={actionConfig.buttonColor}
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
                    Message:
                  </Typography>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {query.message}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Remarks Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Please add remarks to {actionConfig.buttonText}
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder={`Enter detailed remarks for ${actionConfig.buttonText.toLowerCase()}ing this query. Minimum 10 characters required.`}
            value={remarks}
            onChange={handleRemarksChange}
            disabled={loading}
            error={!!error}
            helperText={
              error || 
              `${charCount}/${MAX_REMARKS_LENGTH} characters ${charCount < 10 ? '(minimum 10 required)' : ''}`
            }
            inputProps={{
              maxLength: MAX_REMARKS_LENGTH
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2, bgcolor: 'grey.50' }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color={actionConfig.buttonColor}
          startIcon={actionConfig.buttonIcon}
          disabled={loading || !remarks.trim() || remarks.trim().length < 10}
          size="large"
        >
          {loading ? (
            <>
              <Box sx={{ display: 'inline-block', animation: 'spin 1s linear infinite', mr: 1 }}>
                <CircularProgress size={16} color="inherit" />
              </Box>
              Processing...
            </>
          ) : (
            actionConfig.buttonText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QueryActionDialog;
